import React, { useState, useEffect } from 'react';
import { Loader, X, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../services/apiClient';

const ImageSelector = ({ onSelect, onClose }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  async function fetchImages() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.images.list();
      setImages(data.resources || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteImage() {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      await api.images.delete(deleteConfirm.public_id);
      setImages(images.filter(img => img.public_id !== deleteConfirm.public_id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(`Errore: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] flex flex-col shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-brand-dark">Seleziona Immagine da Archivio</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center h-48">
              <Loader className="animate-spin text-brand-main mb-4" size={32} />
              <p className="text-gray-500">Caricamento immagini...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
              Errore: {error}
            </div>
          )}

          {!loading && !error && images.length === 0 && (
            <div className="text-center text-gray-500 py-12">
              Nessuna immagine trovata in archivio
            </div>
          )}

          {!loading && !error && images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map(image => (
                <div
                  key={image.public_id}
                  className="group relative overflow-hidden rounded-lg border-2 border-gray-200 hover:border-brand-main transition-all"
                >
                  <button
                    onClick={() => onSelect(image.url)}
                    className="w-full h-40 relative overflow-hidden block"
                  >
                    <img
                      src={image.url}
                      alt={image.public_id}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-brand-dark bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Seleziona
                      </span>
                    </div>
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 flex justify-between items-end">
                    <p className="text-white text-xs truncate flex-1">
                      {image.filename}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(image);
                      }}
                      className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      title="Cancella"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark">Cancella Immagine</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler cancellare questa immagine? Se è collegata a un'opera, l'opera rimarrà orfana.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteImage}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Cancellazione...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} /> Cancella
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageSelector;
